/**
 * ADLReopenModal
 * Late Doc Phase 2 — Supervisor reopens a window-closed record (2-24h post-shift)
 * for late documentation. Captures an optional reason for the audit trail.
 *
 * Mounted from ADLFlowSheetPage; triggered by the supervisor-only
 * "Reopen for Late Documentation" button in the closed-window banner.
 */

import { useState, useEffect } from 'react';
import { COLORS } from '../../config/constants';

export default function ADLReopenModal({
  isOpen,
  residentName,
  shiftType,
  shiftDate,
  reopening,
  error,
  onConfirm,
  onCancel,
}) {
  const [reason, setReason] = useState('');

  // Reset reason whenever modal opens
  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reopen-modal-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div style={{
        background: COLORS.surface,
        borderRadius: 12,
        maxWidth: 480, width: '100%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${COLORS.border}`,
          background: COLORS.warningLight,
        }}>
          <div id="reopen-modal-title" style={{
            fontSize: 16, fontWeight: 700, color: COLORS.warning,
          }}>
            🔓 Reopen Record for Late Documentation
          </div>
          {(residentName || shiftType) && (
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
              {residentName}
              {residentName && shiftType ? ' · ' : ''}
              {shiftType} Shift
              {shiftDate ? ` · ${shiftDate}` : ''}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px' }}>
          <p style={{
            fontSize: 13, color: COLORS.text, lineHeight: 1.5, margin: '0 0 14px',
          }}>
            This grants a 24-hour window to complete missing documentation
            for this shift. All entries created during the window will be
            flagged as late documentation in the audit trail.
          </p>

          <label
            htmlFor="reopen-reason"
            style={{
              display: 'block', fontSize: 12, fontWeight: 600,
              color: COLORS.text, marginBottom: 6,
            }}
          >
            Reason for reopen (optional but recommended)
          </label>
          <textarea
            id="reopen-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={reopening}
            placeholder="e.g., CNA reported missing vitals reading from shift change"
            rows={3}
            maxLength={1000}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 10px', borderRadius: 8,
              border: `1px solid ${COLORS.border}`,
              fontSize: 13, fontFamily: 'inherit',
              resize: 'vertical',
              background: reopening ? COLORS.bg : COLORS.surface,
              color: COLORS.text,
            }}
          />

          {error && (
            <div style={{
              marginTop: 10, padding: '8px 12px', borderRadius: 8,
              background: COLORS.dangerLight,
              border: `1px solid ${COLORS.dangerAlpha}`,
              fontSize: 12, color: COLORS.danger,
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={reopening}
            style={{
              padding: '8px 16px', borderRadius: 8,
              background: 'none', color: COLORS.textMuted,
              border: `1px solid ${COLORS.border}`,
              fontSize: 13, fontWeight: 600,
              cursor: reopening ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim() || null)}
            disabled={reopening}
            style={{
              padding: '8px 16px', borderRadius: 8,
              background: COLORS.warning, color: '#fff',
              border: 'none',
              fontSize: 13, fontWeight: 700,
              cursor: reopening ? 'wait' : 'pointer',
            }}
          >
            {reopening ? 'Reopening…' : 'Confirm Reopen'}
          </button>
        </div>
      </div>
    </div>
  );
}